import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Grid,
  Paper,
  Box,
  Divider,
  IconButton,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip
} from '@mui/material';
import {
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import { doc, getDoc, Timestamp } from 'firebase/firestore';
import { db } from '../../shared/utils/firebase';
import { formatDateSafe } from '../../shared/utils/dateHelpers';
import { LoadingSpinner } from '../../shared/components';

// Define interfaces for the different assessment types
interface PreHealthConsultData {
  client_id: string;
  client_name: string;
  contact_number: string;
  email_address: string;
  client_gender: string;
  client_date_of_birth: string;
  client_nationality: string;
  client_occupation: string;
  client_source: string;
  health_injury_history: boolean;
  health_injury_details: string;
  current_exercise: string;
  training_frequency: string;
  goals: string;
  preferred_timings: string;
  last_updated: Timestamp;
  source: string;
  _table_source: string;
}

interface HealthConsultData {
  client_id: string;
  client_name: string;
  // Add other health consult fields as needed
  last_updated: Timestamp;
  source: string;
  _table_source: string;
}

interface ClientIntakeData {
  submission_timestamp: Timestamp;
  response_id: string;
  raw_response: Record<string, any>;
  mindbody_client_id: string;
  name: string;
  date_of_birth: string;
  gender: string;
  pregnancy_status: string;
  what_is_your_current_menstrual_status: string;
  health_rating: string;
  your_goal: string;
  target_goal_achievement_date: string;
  medical_conditions: string;
  additional_information_medical_conditions: string;
  family_history: string;
  surgical_and_hospitalisation_history: string;
  medications: string;
  vitamins_supplements: string;
  allergies: string;
  injury_status: string;
  exercise: string;
  exercise_volume: string;
  non_exercise_activity: string;
  smoking_history: string;
  alcohol_consumption: string;
  sleep_duration: string;
  sleep_quality: number;
  stress_level: number;
  main_stressors: string;
  digestion: string;
  bowel_frequency: string;
  food_allergies_and_sensitivities: string;
  breakfast: string;
  lunch: string;
  dinner: string;
  snacks: string;
  disclaimer: string;
  last_updated: Timestamp;
  source: string;
  _table_source: string;
}

type AssessmentData = PreHealthConsultData | HealthConsultData | ClientIntakeData;

interface ClientAssessmentViewerProps {
  open: boolean;
  onClose: () => void;
  clientId: string;
  assessmentType: 'pre_health_consult' | 'health_consult' | 'client_intake';
}

// Helper function to format timestamp
const formatTimestamp = (timestamp: Timestamp | undefined): string => {
  if (!timestamp) return 'N/A';
  return formatDateSafe(timestamp, 'dd/MM/yyyy HH:mm');
};

// Helper function to format date strings (yyyy-mm-dd to dd/mm/yyyy)
const formatDateString = (dateString: string): string => {
  if (!dateString || dateString === 'N/A') return 'N/A';
  try {
    // Check if it's in yyyy-mm-dd format
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      const [year, month, day] = dateString.split('-');
      return `${day}/${month}/${year}`;
    }
    return dateString;
  } catch (error) {
    return dateString;
  }
};

// Helper function to format field names for display
const formatFieldName = (fieldName: string): string => {
  return fieldName
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2'); // Handle camelCase
};

// Helper function to determine if a field should be displayed
const shouldDisplayField = (fieldName: string): boolean => {
  const excludedFields = [
    'source', 
    '_table_source', 
    'raw_response',
    'client_id',
    'mindbody_client_id',
    'response_id'
  ];
  
  return !excludedFields.includes(fieldName);
};

// Helper function to format field value for display
const formatFieldValue = (value: any): React.ReactNode => {
  if (value === null || value === undefined) return 'N/A';
  
  if (value instanceof Timestamp) {
    return formatTimestamp(value);
  }
  
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }
  
  // Format date strings if field name contains 'date' or 'birth'
  if (typeof value === 'string' && 
      (value.includes('-') && /^\d{4}-\d{2}-\d{2}$/.test(value))) {
    return formatDateString(value);
  }
  
  if (typeof value === 'object') {
    return (
      <Accordion sx={{ mt: 1 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="body2">View Details</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <pre style={{ whiteSpace: 'pre-wrap', fontSize: '0.875rem' }}>
            {JSON.stringify(value, null, 2)}
          </pre>
        </AccordionDetails>
      </Accordion>
    );
  }
  
  return String(value);
};

const ClientAssessmentViewer: React.FC<ClientAssessmentViewerProps> = ({
  open,
  onClose,
  clientId,
  assessmentType
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [assessmentData, setAssessmentData] = useState<AssessmentData | null>(null);

  useEffect(() => {
    const fetchAssessmentData = async () => {
      if (!open || !clientId) return;
      
      setLoading(true);
      setError(null);
      
      try {
        let docPath = '';
        
        // Determine the correct document path based on assessment type
        switch (assessmentType) {
          case 'pre_health_consult':
            docPath = `pre_health_consult/${clientId}`;
            break;
          case 'health_consult':
            docPath = `health_consult/${clientId}`;
            break;
          case 'client_intake':
            docPath = `client_intake/${clientId}`;
            break;
          default:
            throw new Error('Invalid assessment type');
        }
        
        const docRef = doc(db, docPath);
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
          setAssessmentData(docSnap.data() as AssessmentData);
        } else {
          setError('Assessment data not found');
        }
      } catch (err) {
        setError(`Error fetching assessment data: ${err instanceof Error ? err.message : 'Unknown error'}`);
        console.error('Error fetching assessment data:', err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchAssessmentData();
  }, [open, clientId, assessmentType]);

  // Get assessment title based on type
  const getAssessmentTitle = (): string => {
    switch (assessmentType) {
      case 'pre_health_consult':
        return 'Pre-Health Consult Data';
      case 'health_consult':
        return 'Health Consult Data';
      case 'client_intake':
        return 'Client Intake Form Data';
      default:
        return 'Assessment Data';
    }
  };

  // Group fields by category for better organization
  const getFieldGroups = (): { [key: string]: string[] } => {
    if (!assessmentData) return {};
    
    const groups: { [key: string]: string[] } = {
      'Personal Information': [],
      'Health Information': [],
      'Exercise & Lifestyle': [],
      'Nutrition': [],
      'Other': []
    };
    
    Object.keys(assessmentData).forEach(key => {
      if (!shouldDisplayField(key)) return;
      
      // Categorize fields based on name patterns
      if (key.includes('name') || key.includes('email') || key.includes('contact') || 
          key.includes('gender') || key.includes('birth') || key.includes('nationality') || 
          key.includes('occupation')) {
        groups['Personal Information'].push(key);
      } else if (key.includes('health') || key.includes('medical') || key.includes('injury') || 
                key.includes('condition') || key.includes('allerg') || key.includes('medication')) {
        groups['Health Information'].push(key);
      } else if (key.includes('exercise') || key.includes('activity') || key.includes('training') || 
                key.includes('sleep') || key.includes('stress') || key.includes('smoking') || 
                key.includes('alcohol')) {
        groups['Exercise & Lifestyle'].push(key);
      } else if (key.includes('nutrition') || key.includes('diet') || key.includes('food') || 
                key.includes('breakfast') || key.includes('lunch') || key.includes('dinner') || 
                key.includes('snack') || key.includes('digestion')) {
        groups['Nutrition'].push(key);
      } else {
        groups['Other'].push(key);
      }
    });
    
    // Remove empty groups
    Object.keys(groups).forEach(key => {
      if (groups[key].length === 0) {
        delete groups[key];
      }
    });
    
    return groups;
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      scroll="paper"
      aria-labelledby="assessment-dialog-title"
    >
      <DialogTitle id="assessment-dialog-title">
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">{getAssessmentTitle()}</Typography>
          <IconButton edge="end" color="inherit" onClick={onClose} aria-label="close">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent dividers>
        {loading ? (
          <LoadingSpinner minHeight="200px" />
        ) : error ? (
          <Box p={2}>
            <Typography color="error">{error}</Typography>
          </Box>
        ) : assessmentData ? (
          <>
            {assessmentData.last_updated && (
              <Box mb={2}>
                <Chip 
                  label={`Last Updated: ${formatTimestamp(assessmentData.last_updated)}`} 
                  color="primary" 
                  size="small" 
                />
              </Box>
            )}
            
            {Object.entries(getFieldGroups()).map(([groupName, fields]) => (
              <Paper key={groupName} elevation={1} sx={{ mb: 3, p: 2 }}>
                <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                  {groupName}
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                <Grid container spacing={2}>
                  {fields.map(field => (
                    <Grid item xs={12} sm={6} key={field}>
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          {formatFieldName(field)}
                        </Typography>
                        <Typography variant="body1">
                          {formatFieldValue((assessmentData as any)[field])}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Paper>
            ))}
          </>
        ) : (
          <Typography>No data available</Typography>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose} color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ClientAssessmentViewer;
